import autobind from 'autobind';
import flatten from 'lodash/flatten';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';

import { SubscriptionCreateForm, SubscriptionData, SubscriptionUpdateForm } from 'chaika-frontend-types/frontend-cms';
import { ValidationParams } from '@components/types';

import { RenderedServiceApi } from '@api/RenderedServiceApi';
import { SubscriptionApi } from '@api/SubscriptionApi';

import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';

import { store } from '@store';

import { planService } from '@plan/service/planService';
import { userService } from '@user/services/userService';
import { translate } from '@chaika/i18n/helper';
import { patientService } from '@patient/services/patient/patientService';

interface IWithPlanIds {
    planId: string;
}

class SubscriptionService {
    @autobind
    public async load(actualOn?: string) {
        const ehr = store.ehr;

        const subscriptions = await SubscriptionApi.getList(ehr.id, { actualOn });

        await Promise.all([
            this.loadSubscriptionsPlans(subscriptions),
            this.loadSubscriptionsRenderedServices(subscriptions),
            this.loadSubscriptionsDoctors(subscriptions),
        ]);

        ehr.subscriptions.set(subscriptions);
    }

    @autobind
    public async getListByIds(ids: string[]): Promise<SubscriptionData[]> {
        const subscriptions = ids.length > 0 ? await SubscriptionApi.find({ ids: ids.join() }) : [];

        if (subscriptions.length > 0) {
            await Promise.all([
                this.loadSubscriptionsPlans(subscriptions),
                this.loadSubscriptionsRenderedServices(subscriptions),
                this.loadSubscriptionsDoctors(subscriptions),
            ]);
        }

        return subscriptions;
    }

    @autobind
    public async loadPatientSubscriptions(ehrId: string, actualOn?: string) {
        const patientList = store.patientStore.patientList;
        let patient = patientList.get(ehrId);

        if (!patient) {
            await patientService.loadByIds([ehrId], true);
            patient = patientList.get(ehrId);
        }

        if (patient && !patient?.isSubscriptionsLoaded) {
            const subscriptions = await SubscriptionApi.getList(ehrId, { actualOn });

            await Promise.all([
                this.loadSubscriptionsPlans(subscriptions),
                this.loadSubscriptionsRenderedServices(subscriptions),
                this.loadSubscriptionsDoctors(subscriptions),
            ]);

            patient.subscriptions?.set(subscriptions);
        }
    }

    public getPatientActiveSubscriptionsPayerIds(ehrId: string): string[] {
        const {
            patientStore: { patientList },
            planStore: { planList },
        } = store;
        const subscriptions =
            patientList.get(ehrId)?.subscriptions.getFilteredValues({
                isActual: true,
            }) ?? [];

        const planIds = subscriptions.map(({ planId }) => planId);
        const plans = planList.getValuesByIds(planIds);

        return compact(plans.map(({ payerId }) => payerId));
    }

    public async update(subscriptionId: string, params: SubscriptionUpdateForm) {
        const ehr = store.ehr;

        await SubscriptionApi.update(ehr.id, subscriptionId, params);
        ehr.subscriptions.update(subscriptionId, params);
    }

    public validateUpdateForm(subscriptionId: string, params: SubscriptionUpdateForm): ValidationParams {
        const subscriptions = store.ehr.subscriptions;

        const subscription = subscriptions.getExisting(subscriptionId);
        const hasPlanOverlap = subscriptions.hasPlanOverlap({ ...subscription, ...params });

        return {
            isValid: !hasPlanOverlap,
            message: hasPlanOverlap ? translate('cantCreateSameSubacriptions') : '',
        };
    }

    public validateCreateForm({ planId, startDate, endDate }: SubscriptionCreateForm): ValidationParams {
        const hasPlanOverlap = store.ehr.subscriptions.hasPlanOverlap({ planId, startDate, endDate });

        return {
            isValid: !hasPlanOverlap,
            message: hasPlanOverlap ? translate('cantCreateSameSubacriptions') : '',
        };
    }

    public async create({ startDate, endDate, ...params }: SubscriptionCreateForm) {
        const {
            ehr,
            authUser,
            planStore: { planList },
        } = store;

        await this.loadSubscriptionsPlans([params]);

        const { moneyLimit } = planList.getExisting(params.planId);

        const now = new Date().toISOString();

        const createParams = {
            ...params,
            moneyLimit,
            ehrId: ehr.id,
            authorId: authUser.id,
            startDate: startDate || now,
            endDate: endDate || now,
            creationTime: now,
            updateTime: now,
        };

        await SubscriptionApi.create(ehr.id, createParams);

        ehr.subscriptions.add([
            {
                ...createParams,
                // @ts-expect-error
                status: ehr.subscriptions.calculateSubscriptionStatus(createParams.startDate, createParams.endDate),
            },
        ]);
    }

    private async loadSubscriptionsPlans(items: IWithPlanIds[]) {
        const planIds = items.map(item => item.planId);

        await planService.loadByIds(planIds);
    }

    private async loadSubscriptionsRenderedServices(subscriptions: SubscriptionData[]) {
        const ehr = store.ehr;

        const renderedServiceIds = subscriptions.map(it => it.renderedServiceId);
        const chunkedRenderedServiceIds = getUniqChunkedIds(renderedServiceIds);

        const responseChunks = await Promise.all(
            chunkedRenderedServiceIds.map(ids => RenderedServiceApi.getList({ id: ids.join(',') })),
        );
        const renderedServicesChunks = responseChunks.map(item => item.renderedServices);

        ehr.subscriptionRenderedServices.set(flatten(renderedServicesChunks));
    }

    private async loadSubscriptionsDoctors(subscriptions: SubscriptionData[]) {
        const userIds = uniq(compact(subscriptions.map(subscription => subscription.userId)));

        if (userIds.length > 0) {
            await userService.load();
        }
    }
}

export const subscriptionService = new SubscriptionService();
